import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ShieldCheck, ArrowRight, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import { authService } from '../services/api.service';

const VerifyOTP = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [devOtpAlert, setDevOtpAlert] = useState('');

  useEffect(() => {
    const savedEmail = location.state?.email || sessionStorage.getItem('resetEmail');
    if (!savedEmail) {
      navigate('/admin/forgot-password');
      return;
    }
    setEmail(savedEmail);

    if (location.state?.otp) {
      setDevOtpAlert(`Demo Mode OTP: ${location.state.otp}`);
    }
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      setError('Please enter the complete 6-digit OTP code.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.verifyOTP(email, otp);
      setSuccessMsg(response.message || 'OTP verified successfully!');

      sessionStorage.setItem('verifiedOtp', otp);

      setTimeout(() => {
        navigate('/admin/reset-password', { state: { email, otp } });
      }, 1000);
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setError('');
      setSuccessMsg('');
      const res = await authService.forgotPassword(email);
      setSuccessMsg('A new OTP has been sent to your email.');
      if (res.otp) setDevOtpAlert(`New Demo Mode OTP: ${res.otp}`);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP.');
    }
  };

  return (
    <div className="infra-login-wrapper">
      {/* Left Split Hero Branding */}
      <div className="hero-section">
        <div className="hero-glow-effect"></div>
        <div className="hero-wave-overlay"></div>

        <div className="hero-content-layer">
          <div className="hero-logo">
            <div className="dots-grid-logo">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="dot-red"></div>
              ))}
            </div>
            <div className="logo-text-container">
              <span className="logo-main-text">INFRAEDGE</span>
              <span className="logo-sub-text">RED LIGHT THERAPY</span>
            </div>
          </div>

          <div className="hero-main-body">
            <h1 className="hero-headline">
              Enter <span className="hero-headline-highlight">Verification Code</span>
            </h1>
            <p className="hero-subheadline">
              We've sent a 6-digit verification code to <strong>{email}</strong>. Please enter it below to verify your identity.
            </p>
          </div>

          <div className="hero-footer-text" style={{ fontSize: '12px', color: '#64748b' }}>
            © 2026 InfraEdge Red Light Therapy Inc. All rights reserved.
          </div>
        </div>

      </div>

      {/* Right Split Form */}
      <div className="form-section">
        <div className="login-form-card">
          <div className="form-header">
            <h2 className="form-title">Verify OTP</h2>
            <p className="form-subtitle">Enter the 6-digit code sent to your email.</p>
          </div>

          {devOtpAlert && (
            <div className="form-alert" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }}>
              <ShieldCheck size={16} />
              <span>{devOtpAlert}</span>
            </div>
          )}

          {error && (
            <div className="form-alert">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="form-alert" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }}>
              <CheckCircle2 size={16} />
              <span>{successMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label">6-Digit OTP Code</label>
              <div className="input-field-wrapper">
                <ShieldCheck className="field-icon" size={18} />
                <input
                  type="text"
                  maxLength="6"
                  className="styled-input"
                  placeholder="123456"
                  style={{ letterSpacing: '0.3em', fontSize: '18px', fontWeight: 700, fontFamily: 'monospace' }}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn-red-submit" disabled={loading}>
              {loading ? (
                <div className="spinner-white"></div>
              ) : (
                <>
                  <span>Verify OTP</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '24px', fontSize: '13px' }}>
            <Link to="/admin/forgot-password" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#64748b', fontWeight: 600, textDecoration: 'none' }}>
              <ArrowLeft size={16} />
              <span>Change Email</span>
            </Link>

            <button onClick={handleResend} style={{ background: 'none', border: 'none', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}>
              Resend OTP
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
